"use client";

import { ArrowRight, Droplets, Wrench, Hammer, RefreshCw, ShieldCheck, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Capabilities() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    spec: "[REF: 001-BUILD]",
    title: "01\xA0 BUILD",
    description: "Anyone can dig a hole. We engineer everything that goes in it.\nSpecified on paper. Verified in the field. Signed off at every step.",
    Icon: Hammer
  }, {
    spec: "[REF: 002-SERV]",
    title: "02\xA0 SERVICE",
    description: "Green pools are not bad luck. They are bad chemistry.\nBalanced to an LSI of 0.0. Calibrated digital testing. Every single visit.",
    Icon: Droplets
  }, {
    spec: "[REF: 003-REPR]",
    title: "03 REPAIR",
    description: "Most repairs fail twice because nobody found the cause the first time.\nDiagnosed to the root. Priced before we touch it. Fixed once.",
    Icon: Wrench
  }, {
    spec: "[REF: 004-RENO]",
    title: "04 RENOVATIONS",
    description: "Resurfacing a bad pool gets you a good-looking bad pool.\nWe correct the structure and the hydraulics, then we make it beautiful.",
    Icon: RefreshCw
  }, {
    spec: "[REF: 005-INSP]",
    title: "05 INSPECTIONS",
    description: "Leaks, squeaks, and everything in between.\nFull systems evaluation in writing, before you buy or before it gets worse.",
    Icon: ShieldCheck
  }, {
    spec: "[REF: 006-LEGAL]",
    title: "06 LEGAL",
    description: "Every term we operate under, published where you can read it.\nPayments, refunds, messaging, privacy. No surprises in the fine print.",
    Icon: Zap
  }];
  return <section data-section-id="1791" id="core-capabilities" className="relative bg-background py-24 md:py-48 overflow-hidden">
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-6 bg-primary" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(240 10% 3.9%) 0, hsl(240 10% 3.9%) 4px, hsl(240 10% 6%) 4px, hsl(240 10% 6%) 8px)"
    }} />

      <div className="max-w-[1360px] mx-auto px-6 md:px-8 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-16 md:mb-24 transition-all duration-500 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2.5 h-2.5 bg-primary" />
              <span className="font-mono text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-foreground">
                § 02 — Core Capabilities
              </span>
            </div>

            <h2 className="font-sans font-bold uppercase text-foreground leading-[1.1] tracking-tighter text-5xl md:text-6xl lg:text-7xl">
              Precision <em className="not-italic text-primary">aquatic engineering</em> for your property.
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-end">
            <p className="font-mono text-sm leading-relaxed uppercase tracking-wider text-muted-foreground mb-6">[SPEC-02 / CATALOG] FIVE DISCIPLINES. ONE STANDARD. NEW CONSTRUCTION, RENOVATION, SERVICE, REPAIR, AND EQUIPMENT, DELIVERED UNDER THE SAME ENGINEERED SPEC.</p>
            <Link to="#" className="group inline-flex items-center gap-2 font-mono text-sm font-semibold uppercase tracking-wider text-foreground hover:text-primary transition-colors duration-200">
              <span>View our services</span>
              <ArrowRight className="w-4 h-4 text-primary transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div aria-hidden="true" className="h-1.5 w-32 mb-0" style={{
        backgroundImage: "repeating-linear-gradient(45deg, hsl(191 91% 47%) 0, hsl(191 91% 47%) 12px, hsl(240 10% 3.9%) 12px, hsl(240 10% 3.9%) 24px)"
      }} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <article key={i} data-index={i} className="group relative bg-card border-r border-b border-border p-8 min-h-[280px] flex flex-col transition-colors duration-300 hover:border-primary cursor-pointer">
                <div className="flex items-start justify-between mb-auto">
                  <Icon className="w-9 h-9 stroke-[1.5] text-foreground transition-colors duration-200 group-hover:text-primary" strokeWidth={1.5} />
                  <span className="font-mono text-xs font-medium tracking-[0.12em] uppercase text-muted-foreground transition-colors duration-300 group-hover:text-primary">
                    {service.spec}
                  </span>
                </div>

                <div className="relative inline-block mt-8 mb-3">
                  <h3 className="font-sans font-semibold uppercase text-2xl md:text-[1.75rem] leading-none tracking-tight text-foreground transition-colors duration-300 group-hover:text-primary">
                    {service.title}
                  </h3>
                </div>

                <p className="font-sans text-base leading-relaxed text-muted-foreground transition-colors duration-300 mt-4">
                  {service.description}
                </p>

                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-wider text-foreground transition-colors duration-300 group-hover:text-primary">
                    <ArrowRight className="w-3.5 h-3.5" />
                    Details
                  </span>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}